/*
  Perfect Pass product page (storefront redesign, 09-2026). Loaded by the pp-fits, pp-trust and pp-buy-bar snippets.
  - <pp-fits>: marks the visitor's saved car (assets/pp-car.js) and says whether this part is listed for it.
  - <pp-trust>: "Same Day Shipping" only while the chosen variant is in stock.
  - <pp-buy-bar>: phone buy bar that mirrors the chosen variant and presses the page's own Add to cart button.
  All three follow Dawn's variant-change event (assets/pubsub.js, PUB_SUB_EVENTS.variantChange).
*/
(() => {
  if (customElements.get('pp-buy-bar')) return;

  // only the main product counts (quick-add pop-ups publish the same event for other products)
  const mainProductInfo = () => document.querySelector('product-info[id^="MainProduct-"]');
  // Dawn's subscribe / PUB_SUB_EVENTS come from deferred scripts; wait for them if this file ran first
  const onVariantChange = (callback) => {
    let unsubscribe = () => {};
    const attach = () => {
      if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
      unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
        const main = mainProductInfo();
        const mainSection = main?.dataset.originalSection || main?.dataset.section;
        if (event?.data?.variant && event.data.sectionId === mainSection) callback(event.data.variant);
      });
    };
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', attach, { once: true });
    else attach();
    return () => unsubscribe();
  };

  // ---------- Fits ----------
  // A part fits the saved car when its tags name that car, say "Any car", or say "All Arrma 1/7" and the car is one of them.
  const ARRMA_17 = ['Limitless', 'Infraction', 'Felony', 'All Arrma 1/7'];
  const ANY_CAR = 'Any car';
  const PICK_ANY = 'Fits any car'; // the homepage chip for "show me everything"

  customElements.define(
    'pp-fits',
    class PpFits extends HTMLElement {
      connectedCallback() {
        this.fits = (this.dataset.fits || '').split('|').filter(Boolean);
        this.status = this.querySelector('.pp-fits__status');
        this.onCarChange = () => this.render();
        document.addEventListener('pp:car-change', this.onCarChange);
        this.render();
      }

      disconnectedCallback() {
        document.removeEventListener('pp:car-change', this.onCarChange);
      }

      render() {
        const car = window.ppCar?.get()?.name;
        this.querySelectorAll('.pp-fits__chip').forEach((chip) => {
          const yours = car && (chip.dataset.car === car || (chip.dataset.car === 'All Arrma 1/7' && ARRMA_17.includes(car)));
          chip.classList.toggle('is-yours', Boolean(yours));
        });
        if (!car || car === PICK_ANY) {
          this.status.hidden = true;
          return;
        }
        const listed =
          this.fits.includes(car) ||
          this.fits.includes(ANY_CAR) ||
          (this.fits.includes('All Arrma 1/7') && ARRMA_17.includes(car));
        const change = '<a href="/#what-do-you-drive">Change car</a>';
        this.status.classList.toggle('is-warning', !listed);
        this.status.innerHTML = listed
          ? `<span aria-hidden="true">✓</span> Fits your ${escapeHtml(car)} · ${change}`
          : `Not listed for your ${escapeHtml(car)}. Check the description or ask us before you order. · ${change}`;
        this.status.hidden = false;
      }
    }
  );

  const escapeHtml = (text) =>
    String(text).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]);

  // ---------- Trust lines ----------
  customElements.define(
    'pp-trust',
    class PpTrust extends HTMLElement {
      connectedCallback() {
        this.unsubscribe = onVariantChange((variant) => {
          const sameDay = this.querySelector('[data-same-day]');
          if (sameDay) sameDay.hidden = !variant.available;
        });
      }

      disconnectedCallback() {
        this.unsubscribe?.();
      }
    }
  );

  // ---------- Phone buy bar ----------
  customElements.define(
    'pp-buy-bar',
    class PpBuyBar extends HTMLElement {
      connectedCallback() {
        // Dawn's quick-add sheet copies the whole product page, this bar included; the bar belongs to the product
        // page only, so inside a sheet it removes itself before it can move out onto the home, collection or
        // search page and stay there as a dead gold bar (launch review 10-21, T1-A).
        if (this.closest('quick-add-modal')) {
          this.remove();
          return;
        }
        // position: fixed only pins to the screen when no ancestor is transformed; Dawn's animated product column is,
        // so the bar moves itself to <body>. Any bar left there by an earlier render (theme editor) is removed.
        if (this.parentElement !== document.body) {
          document.querySelectorAll('body > pp-buy-bar').forEach((bar) => bar.remove());
          document.body.append(this);
          return;
        }
        this.button = this.querySelector('[data-buy]');
        this.onClick = () => this.buy();
        this.button.addEventListener('click', this.onClick);
        this.unsubscribe = onVariantChange((variant) => this.update(variant));
        document.body.classList.add('pp-has-buy-bar');
        this.syncPrice();
      }

      disconnectedCallback() {
        if (!this.button) return; // moved before it was set up
        this.button.removeEventListener('click', this.onClick);
        this.unsubscribe?.();
        this.button = null;
        document.body.classList.remove('pp-has-buy-bar');
      }

      // the product's own form in the main product section (not a quick-add modal, not the installments form)
      get mainForm() {
        return mainProductInfo()?.querySelector('product-form form[action$="/cart/add"]');
      }

      buy() {
        const form = this.mainForm;
        const submit = form?.querySelector('[type="submit"][name="add"]');
        if (!submit) return;
        // Dawn disables its button while it adds (aria-disabled + .loading) and while it fetches a new option
        // (disabled, no text change); neither means sold out, so a second tap just waits (review 10-21, T2-11).
        // Sold out is what the variant said last (pp-buy-bar__button--sold, set by Liquid and update()).
        const busy = submit.classList.contains('loading') || submit.getAttribute('aria-disabled') === 'true';
        if (busy) return;
        if (this.button.classList.contains('pp-buy-bar__button--sold')) {
          // sold out: open the notify-me form (Restock Rocket's button in the buy block); without the app,
          // take them to the variant picker (Bar's QA 09-30, M07: the bar's Notify me did nothing visible)
          const notify = form.closest('.product__info-container')?.querySelector('.restock-rocket-button-product');
          if (notify) notify.click();
          else form.closest('.product__info-container')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
          return;
        }
        if (submit.disabled) return; // an option fetch is in flight; the next tap lands on the new variant
        submit.click();
      }

      // the page's own price, in the store's format (with or without the currency code)
      syncPrice() {
        const price = this.querySelector('[data-price]');
        const main = mainProductInfo()?.querySelector('.price');
        if (!price || !main) return;
        const current = main.classList.contains('price--on-sale')
          ? main.querySelector('.price__sale .price-item--last')
          : main.querySelector('.price__regular .price-item--regular');
        if (current) price.textContent = current.textContent.trim();
      }

      update(variant) {
        this.syncPrice();
        const variantName = this.querySelector('[data-variant]');
        if (variantName) variantName.textContent = variant.title && variant.title !== 'Default Title' ? `${variant.title} · ` : '';
        const stock = this.querySelector('[data-stock]');
        if (stock) stock.textContent = variant.available ? 'In stock' : this.button.dataset.stockSold;
        this.button.textContent = variant.available ? this.button.dataset.labelAdd : this.button.dataset.labelSold;
        this.button.classList.toggle('pp-buy-bar__button--sold', !variant.available);
      }
    }
  );
})();
